import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import StatCard from '../components/admin/StatCard';
import {
  IconBuilding, IconCheckCircle, IconUsers, IconLink,
  IconHeadset, IconTrash, IconSearch,
} from '../components/admin/icons';
import { useI18n } from '../i18n';

interface Client {
  id: number;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
  installerCount: number;
  memberCount: number;
}

export default function AdminClientsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t, tp, locale } = useI18n();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteModal, setDeleteModal] = useState<{ orgId: number; name: string } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Support mode (1-click, no modal)
  const [supportError, setSupportError] = useState<string | null>(null);
  const [supportSubmitting, setSupportSubmitting] = useState(false);

  const API = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

  useEffect(() => {
    fetch(`${API}/api/admin/clients`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { if (data.success) setClients(data.clients); })
      .finally(() => setLoading(false));
  }, []);

  const openDeleteModal = (orgId: number, name: string) => {
    setDeleteModal({ orgId, name });
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    if (deleteConfirmText !== deleteModal.name) { setDeleteError(t('adm_cli_name_mismatch')); return; }
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`${API}/api/organizations/${deleteModal.orgId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setClients(prev => prev.filter(c => c.id !== deleteModal!.orgId));
        setDeleteModal(null);
      } else {
        setDeleteError(typeof data?.error === 'string' ? data.error : data?.error?.message || t('adm_cli_delete_error'));
      }
    } catch {
      setDeleteError(t('adm_err_network'));
    } finally {
      setDeleting(false);
    }
  };

  const filtered = clients.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  const enterSupport = async (orgId: number, name: string) => {
    setSupportError(null);
    setSupportSubmitting(true);
    try {
      const res = await fetch(`${API}/api/admin/support/access`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId: orgId,
          reason: `Accès super-admin au dashboard de ${name} — ${new Date().toLocaleString('fr-FR')}`,
          action: 'view_dashboard',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        setSupportError(typeof data?.error === 'string' ? data.error : t('adm_err_status', { status: res.status }));
        setSupportSubmitting(false);
        return;
      }
      sessionStorage.setItem('actingClientOrgId', String(orgId));
      sessionStorage.setItem('actingClientName', name);
      if (data.sessionId) sessionStorage.setItem('supportSessionId', String(data.sessionId));
      navigate('/wattel-em');
    } catch (e: any) {
      setSupportError(e?.message || t('adm_err_network'));
      setSupportSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" /></div>;
  }

  const activeCount = clients.filter(c => c.status === 'active').length;
  const totalMembers = clients.reduce((s, c) => s + c.memberCount, 0);
  const totalInstallers = clients.reduce((s, c) => s + c.installerCount, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl text-white shadow-2xl">
        {/* Image de fond — gratte-ciel / business buildings */}
        <img
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&h=600&fit=crop"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/90 to-teal-950/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
        {/* Pattern grille subtile */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        {/* Halos colorés */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-teal-500/30 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-cyan-500/20 blur-3xl" />

        <div className="relative p-7">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-5">
              <div className="relative shrink-0">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 blur-lg opacity-60" />
                <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg ring-1 ring-white/20 text-white">
                  <IconBuilding className="w-7 h-7" />
                </div>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.08] backdrop-blur ring-1 ring-cyan-400/30 text-cyan-300 text-[11px] font-semibold mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  {t('adm_console_badge')}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white drop-shadow-lg">
                  {t('adm_cli_title_1')}{' '}
                  <span className="bg-gradient-to-r from-teal-300 via-cyan-300 to-sky-300 bg-clip-text text-transparent">
                    {t('adm_cli_title_2')}
                  </span>
                </h1>
                <p className="text-slate-200 text-sm mt-1.5 drop-shadow">
                  {tp('adm_cli_connected', clients.length)}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <StatCard icon={IconCheckCircle} label={t('adm_cli_stat_active')} value={activeCount} color="emerald" total={clients.length || 1} compact />
            <StatCard icon={IconBuilding} label={t('adm_cli_stat_total')} value={clients.length} color="teal" hint={t('adm_cli_stat_total_hint')} compact />
            <StatCard icon={IconUsers} label={t('adm_cli_stat_members')} value={totalMembers} color="cyan" hint={t('adm_cli_stat_members_hint')} compact />
            <StatCard icon={IconLink} label={t('adm_cli_stat_links')} value={totalInstallers} color="violet" hint={t('adm_cli_stat_links_hint')} compact />
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-slate-800">{t('adm_cli_list')}</h2>
        <div className="relative">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder={t('adm_cli_search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 w-72 bg-white"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-5 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('adm_cli_th_org')}</th>
              <th className="text-center px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('adm_cli_th_members')}</th>
              <th className="text-center px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('adm_nav_installers')}</th>
              <th className="text-center px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('adm_th_status')}</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('adm_cli_th_joined')}</th>
              <th className="text-right px-5 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('adm_th_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(client => {
              const initials = client.name.slice(0, 2).toUpperCase();
              return (
                <tr key={client.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{client.name}</p>
                        <p className="text-[11px] text-slate-400 truncate">{client.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-center text-slate-700 font-medium">{client.memberCount}</td>
                  <td className="px-4 py-3.5 text-center text-slate-700 font-medium">{client.installerCount}</td>
                  <td className="px-4 py-3.5 text-center">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                      client.status === 'active'
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${client.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                      {client.status === 'active' ? t('adm_cli_st_active') : client.status}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-slate-600 text-xs">
                    {new Date(client.createdAt).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        onClick={() => enterSupport(client.id, client.name)}
                        disabled={supportSubmitting}
                        className="px-3 py-1.5 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-wait"
                      >
                        <IconHeadset className="w-3.5 h-3.5" />
                        {t('adm_cli_support_mode')}
                      </button>
                      <button
                        onClick={() => openDeleteModal(client.id, client.name)}
                        className="px-3 py-1.5 text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-colors inline-flex items-center gap-1.5"
                      >
                        <IconTrash className="w-3.5 h-3.5" />
                        {t('adm_delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3 text-slate-400">
              <IconSearch className="w-7 h-7" />
            </div>
            <p className="text-slate-500 text-sm">{t('adm_cli_empty')}</p>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setDeleteModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-red-50 px-6 py-4 border-b border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{t('adm_cli_delete_title')}</h3>
                  <p className="text-sm text-gray-500">{t('adm_cli_irreversible')}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-700">
                {t('adm_cli_delete_text_1')} <strong className="text-red-600">"{deleteModal.name}"</strong> {t('adm_cli_delete_text_2')}
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  {t('adm_cli_type_1')} <span className="text-red-600">"{deleteModal.name}"</span> {t('adm_cli_type_2')}
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && confirmDelete()}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-300 focus:border-red-400 transition-all"
                  placeholder={deleteModal.name}
                  autoFocus
                />
              </div>
              {deleteError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {deleteError}
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t flex gap-3">
              <button
                onClick={() => setDeleteModal(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 transition-all"
              >
                {t('adm_cancel')}
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting || deleteConfirmText !== deleteModal.name}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all inline-flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg> {t('adm_cli_deleting')}</>
                ) : (
                  <>🗑️ {t('adm_cli_confirm_delete')}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast d'erreur mode support (non-bloquant) */}
      {supportError && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm bg-white border border-rose-200 rounded-xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4">
          <div className="flex items-start gap-3 p-4">
            <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center text-rose-600 shrink-0">⚠️</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">{t('adm_cli_support_failed')}</p>
              <p className="text-xs text-slate-600 mt-0.5 break-words">{supportError}</p>
            </div>
            <button
              onClick={() => setSupportError(null)}
              aria-label={t('adm_close')}
              className="text-slate-400 hover:text-slate-600 text-lg leading-none"
            >
              &times;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
